import React, { useState, useEffect } from 'react';
import {Card, Input, Divider, Checkbox, Tag, Form, Select, Tooltip} from "antd";
import {AddCircleRounded, ScheduleRounded, ShieldRounded} from "@mui/icons-material";
import Button from "@common/Button";
import axios from "@common/axios";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import TableFilterControlsForm from "@common/TableFilterControlsForm";

const UserSelector = ({
                          onSelectedUsersChange,
                          selectedUsers = [],
                          onAddUsers = null,
                          showAddButton = false,
                          existingUserIds = []
                      }) => {
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUserTypes, setSelectedUserTypes] = useState(['TEACHER', 'STUDENT']);

    useEffect(() => {
        axios.get('/web/ui/sc/users')
            .then(response => {
                const usersObj = response.data;
                const usersArray = Object.values(usersObj);
                // Filter to only include enabled users
                const enabledUsers = usersArray.filter(user => user.enabled === true)
                    .sort((a, b) => a.fullName.localeCompare(b.fullName, undefined, { sensitivity: 'base' }));
                setAllUsers(enabledUsers);
                setFilteredUsers(enabledUsers);
            })
            .finally(() => {
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching users:', error);
            });
    }, []);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        filterUsers(value, selectedUserTypes);
    };

    const handleUserTypeChange = (types) => {
        setSelectedUserTypes(types);
        filterUsers(searchValue, types);
    };

    const filterUsers = (search, userTypes) => {
        let filtered = allUsers;

        // Filter by user type
        if (userTypes && userTypes.length > 0) {
            filtered = filtered.filter(user => userTypes.includes(user.subscriberType));
        }

        // Filter by search value
        if (search) {
            filtered = filtered.filter(user =>
                user.fullName?.toLowerCase().includes(search.toLowerCase()) ||
                user.username?.toLowerCase().includes(search.toLowerCase()) ||
                user.email?.toLowerCase().includes(search.toLowerCase())
            );
        }

        setFilteredUsers(filtered);
    };

    const handleUserToggle = (user, checked) => {
        const userSelection = {
            value: user.userId.toString(),
            label: user.fullName,
            user: user
        };

        let newSelectedUsers;
        if (checked) {
            newSelectedUsers = [...selectedUsers, userSelection];
        } else {
            newSelectedUsers = selectedUsers.filter(item => item.value !== user.userId.toString());
        }

        onSelectedUsersChange(newSelectedUsers);
    };

    const isUserSelected = (userId) => {
        return selectedUsers.some(item => item.value === userId.toString());
    };

    const isUserInClass = (userId) => {
        return existingUserIds.includes(userId);
    };

    const handleAddUsers = () => {
        if (onAddUsers) {
            onAddUsers(selectedUsers);
        }
    };

    const renderUserCard = (user, idx) => {
        const userAlreadyInClass = isUserInClass(user.userId);
        const selected = isUserSelected(user.userId);
        const clickable = !userAlreadyInClass;

        return (
            <Card key={user.userId || idx}
                  className={'ch-manage-users-selector-card'}
                  size={"small"}
                  style={{
                      ...(clickable ? {cursor: 'pointer'} : {}),
                      ...(selected ? {outline: '2px solid var(--input-colour)', outlineOffset: '-2px'} : {})
                  }}
                  onClick={clickable ? () => handleUserToggle(user, !selected) : undefined}
            >
                <div className={'ch-manage-users-selector-card-content'}>
                    <div className={'ch-manager-users-selector-card-heading'}>
                        <h3>
                            {user.fullName}
                            {user.schoolAdmin &&
                                <Tooltip title={'Admin user'}>
                                <ShieldRounded fontSize={'small'}
                                               style={{marginLeft: '8px', color: '#1890ff'}}/>
                                </Tooltip>
                            }
                            {user.expired &&
                                <Tooltip title={'User is expired'}>
                                    <ScheduleRounded fontSize={'small'} style={{ marginLeft: '8px', color: '#1890ff' }} />
                                </Tooltip>
                            }
                        </h3>
                        <p>{user.email} · <span>{user.subscriberType.toLowerCase()}</span></p>
                    </div>
                    <div className={'ch-manage-users-selector-cards-actions'}
                         onClick={clickable ? (e) => e.stopPropagation() : undefined}>
                        {userAlreadyInClass ? (
                            <Tag color={'green'} variant={'solid'}>Already in class</Tag>
                        ) : (
                            <Checkbox
                                checked={selected}
                                onChange={(e) => handleUserToggle(user, e.target.checked)}
                                className={'ch-manage-users-selector-checkbox'}
                            />
                        )}
                    </div>
                </div>
            </Card>
        );
    };

    if (loading) {
        return <div>Loading users...</div>;
    }

    return (
        <>
            <div className={'ch-manage-users-selector-header'}>
                <h3>Users in school</h3>
                <TableFilterControls filterContent={
                    <TableFilterControlsForm>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item label="Search for users by name or email">
                                <Input
                                    id="email-search"
                                    placeholder="student@school.com"
                                    value={searchValue}
                                    onChange={handleSearch}
                                />
                            </Form.Item>
                            <Form.Item label="User type">
                                <Select
                                    mode="multiple"
                                    placeholder="Select user types"
                                    value={selectedUserTypes}
                                    onChange={handleUserTypeChange}
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                    options={[
                                        { label: 'Teacher', value: 'TEACHER' },
                                        { label: 'Student', value: 'STUDENT' }
                                    ]}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>
                }/>
            </div>
            <div className={'ch-manage-users-selector-card-wrapper'}>
                {filteredUsers.length === 0 ? (
                    <div className={'ch-manage-users-selector-no-result'}>
                        {searchValue ? 'No users found matching your search.' : 'No users found.'}
                    </div>
                ) : (
                    filteredUsers.map((user, idx) => renderUserCard(user, idx))
                )}
                {showAddButton &&
                    <Button
                        type={'outline'}
                        className={'ch-manager-users-selector-add-user-button'}
                        onClick={handleAddUsers}
                        disabled={!selectedUsers.length > 0}
                    >
                        Add {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} to class
                    </Button>
                }
            </div>
        </>
    );
};

export default UserSelector;